/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ============================================================================
 *   Module       : useCurrentTenant
 *   Feature      : 241-tenant-read-isolation
 *   Description  : The tenant whose data the current session is reading, for
 *                  display in the app header.
 *   Author       : Peter Dobler
 * ============================================================================
 *
 *   🛑 THE TENANT IS PER-SESSION, NOT PER-DEPLOYMENT. It comes from the login →
 *      `dbo.users.platform_tenant_id` association, resolved server-side per request.
 *      Never source it from a build-time variable, a global setting or a Key Vault
 *      secret: this app serves EVERY tenant from one deployment, so any process-wide
 *      value would show every customer the same name — and the wrong one the moment a
 *      second tenant exists. That mistake was made once already on this epic and was
 *      invisible precisely because the two values are equal today.
 */
import { useEffect, useState } from 'react';
import { apiClient } from '../services/apiClient';

export interface CurrentTenant {
  id: string | null;
  name: string | null;
  status?: string | null;
}

export function useCurrentTenant(): { tenant: CurrentTenant | null; loading: boolean } {
  const [tenant, setTenant] = useState<CurrentTenant | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<CurrentTenant>('/api/auth/tenant')
      .then((res) => {
        if (!cancelled) setTenant(res.data ?? null);
      })
      // Fail quiet: the header is an affordance, not a gate. A tenant we cannot name
      // renders as nothing rather than blocking the shell behind an error.
      .catch(() => {
        if (!cancelled) setTenant(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { tenant, loading };
}

export default useCurrentTenant;
